import { ExpandMore } from "@mui/icons-material";
import { Button, CardActions, CardContent, CircularProgress, Collapse, Stack } from "@mui/material";
import { JSX } from "react";
import { useTranslation } from "react-i18next";
import NoData from "../../../components/no-data/NoData";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useUICustomizationPresetUsers } from "../../../queries/ui-customization/UseUICustomizationPresetUsers";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { IUICustomizationPresetDescriptionDTO } from "../../../services/ui-customization-data-service/Models";
import UserCard from "../../user-role-administration-view/common/core/UserCard";
import UICustomizationPresetCard from "./UICustomizationPresetCard";

export type UICustomizationPresetListItemProps = {
    organizationID: number | undefined;
    preset: IUICustomizationPresetDescriptionDTO;
    onEditPreset?: (preset: IUICustomizationPresetDescriptionDTO) => void;
    onDeletePreset?: (preset: IUICustomizationPresetDescriptionDTO) => void;
    sectionExpansion: { [key: string]: boolean; };
    toggleSectionExpansion: (key: string) => void;
};

export default function UICustomizationPresetListItem({
    organizationID,
    preset,
    onEditPreset,
    onDeletePreset,
    sectionExpansion,
    toggleSectionExpansion,
}: UICustomizationPresetListItemProps): JSX.Element {

    const { t } = useTranslation();
    const routeContext = useRouteContext();
    const membershipSectionKey = `M:${preset.id}`;
    const membershipSectionExpanded = sectionExpansion[membershipSectionKey] ?? false;

    const {
        data: userData,
        status: userStatus
    } = useQueryNotifications(useUICustomizationPresetUsers)({
        groupID: routeContext.groupID as number,
        presetID: preset.id,
        enabled: membershipSectionExpanded
    });

    return (
        <UICustomizationPresetCard
            preset={preset}
            variant="outlined"
            onEdit={onEditPreset}
            onDelete={onDeletePreset}
        >
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <Button
                    sx={{
                        color: "text.primary"
                    }}
                    variant="text"
                    onClick={() => toggleSectionExpansion(membershipSectionKey)}
                    style={{ marginLeft: "auto" }}
                    endIcon={
                        userStatus === "loading"
                            ? <CircularProgress size={20} />
                            : <ExpandMore
                                sx={{
                                    transform: !membershipSectionExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                    transition: theme => theme.transitions.create('transform', {
                                        duration: theme.transitions.duration.shortest,
                                    })
                                }}
                            />
                    }
                >
                    {`${t("userRoleAdministration.membership")}[${preset.userCount ?? "0"}]`}
                </Button>
            </CardActions>

            <Collapse in={membershipSectionExpanded} timeout="auto">
                <CardContent sx={{
                    padding: (theme) => `0 ${theme.spacing(2)} ${theme.spacing(1)} ${theme.spacing(2)} !important`
                }}>
                    {userStatus !== "loading" && (userData?.length ?? 0) === 0 &&
                        <NoData
                            title={t("uiCustomizationPresetAdministration.tabs.presets.noMembers.title")}
                            text={t("uiCustomizationPresetAdministration.tabs.presets.noMembers.text")}
                        />
                    }
                    <Stack direction="column" spacing={1} >
                        {userData?.map((o, index) => {
                            return (
                                <UserCard
                                    key={index}
                                    user={o}
                                    variant="elevation"
                                    elevation={2}
                                />
                            );
                        })}
                    </Stack>
                </CardContent>
            </Collapse>
        </ UICustomizationPresetCard>
    );
}



